import { useEffect, useRef, useState } from "react";

/**
 * Whether decorative motion may run. Gated on `matchMedia` so we honour
 * `prefers-reduced-motion`; when it (or rAF) is unavailable — jsdom, SSR — callers
 * render the final state immediately instead of animating towards it.
 */
export function canAnimate(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.requestAnimationFrame === "function" &&
    typeof window.matchMedia === "function" &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export interface CountUpOptions {
  durationMs: number;
  /** Holds the figure at its start value so a count can begin in step with a delayed CSS entrance. */
  delayMs?: number;
}

/**
 * Eases a whole number towards `target` with easeOutQuint — the JS twin of the
 * `cubic-bezier(0.22, 1, 0.36, 1)` curve our CSS entrances use, so a counting figure and
 * a growing bar stay in lockstep. Counts from zero on mount and from whatever is on
 * screen when the target changes; renders the final value outright when motion is reduced.
 */
export function useCountUp(
  target: number,
  { durationMs, delayMs = 0 }: CountUpOptions,
): number {
  const [display, setDisplay] = useState(() => (canAnimate() ? 0 : target));
  const displayRef = useRef(display);
  const hasCountedRef = useRef(false);

  useEffect(() => {
    if (!canAnimate()) {
      displayRef.current = target;
      setDisplay(target);
      return;
    }

    const from = displayRef.current;
    if (from === target) return;

    // The delay lines the first count up with a delayed entrance animation. A later value
    // change tracks that element's transition instead, which carries no delay.
    const holdMs = hasCountedRef.current ? 0 : delayMs;
    hasCountedRef.current = true;

    let raf = 0;
    // Anchor on the first frame: rAF timestamps and `performance.now()` can carry
    // different time origins, which would send the first progress reading off-scale.
    let start: number | null = null;
    const tick = (now: number) => {
      start ??= now;
      const elapsed = now - start - holdMs;
      if (elapsed >= 0) {
        const progress = Math.min(elapsed / durationMs, 1);
        const eased = 1 - Math.pow(1 - progress, 5);
        displayRef.current = Math.round(from + (target - from) * eased);
        setDisplay(displayRef.current);
        if (progress === 1) return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, delayMs]);

  return display;
}
